import {
  readOnlineOrderAlertSettings,
  type OnlineOrderTuneId,
} from './onlineOrderAlertSettings';

type NoteStep = {
  freq: number;
  start: number;
  duration: number;
  type?: OscillatorType;
  /** Per-note peak gain; short tunes default to 0.4, longer tunes use ~0.75–0.9. */
  gain?: number;
};

const SHORT_TUNE_GAIN = 0.4;
const LONG_TUNE_GAIN = 0.82;

/** Tunes 1–10: short taps. Tunes 11–15: ~1.2–1.5s louder alert patterns. */
const TUNE_SEQUENCES: Record<OnlineOrderTuneId, NoteStep[]> = {
  'tune-1': [{ freq: 880, start: 0, duration: 0.07, type: 'sine' }],
  'tune-2': [{ freq: 660, start: 0, duration: 0.08, type: 'triangle' }],
  'tune-3': [{ freq: 520, start: 0, duration: 0.07, type: 'sine' }],
  'tune-4': [
    { freq: 740, start: 0, duration: 0.05, type: 'square' },
    { freq: 988, start: 0.07, duration: 0.05, type: 'square' },
  ],
  'tune-5': [{ freq: 620, start: 0, duration: 0.06, type: 'square' }],
  'tune-6': [{ freq: 523, start: 0, duration: 0.08, type: 'triangle' }],
  'tune-7': [{ freq: 1400, start: 0, duration: 0.05, type: 'square' }],
  'tune-8': [{ freq: 587, start: 0, duration: 0.07, type: 'sine' }],
  'tune-9': [{ freq: 1760, start: 0, duration: 0.04, type: 'sine' }],
  'tune-10': [
    { freq: 880, start: 0, duration: 0.04, type: 'sawtooth' },
    { freq: 880, start: 0.06, duration: 0.04, type: 'sawtooth' },
  ],
  'tune-11': [
    { freq: 520, start: 0, duration: 0.28, type: 'sine', gain: LONG_TUNE_GAIN },
    { freq: 660, start: 0.34, duration: 0.28, type: 'sine', gain: LONG_TUNE_GAIN },
    { freq: 880, start: 0.68, duration: 0.28, type: 'sine', gain: 0.86 },
    { freq: 1100, start: 1.02, duration: 0.32, type: 'sine', gain: 0.9 },
  ],
  'tune-12': [
    { freq: 800, start: 0, duration: 0.25, type: 'square', gain: LONG_TUNE_GAIN },
    { freq: 800, start: 0.35, duration: 0.25, type: 'square', gain: LONG_TUNE_GAIN },
    { freq: 800, start: 0.7, duration: 0.25, type: 'square', gain: LONG_TUNE_GAIN },
    { freq: 800, start: 1.05, duration: 0.3, type: 'square', gain: 0.88 },
  ],
  'tune-13': [
    { freq: 440, start: 0, duration: 0.62, type: 'triangle', gain: LONG_TUNE_GAIN },
    { freq: 660, start: 0.72, duration: 0.62, type: 'triangle', gain: 0.88 },
  ],
  'tune-14': [
    { freq: 620, start: 0, duration: 0.26, type: 'square', gain: LONG_TUNE_GAIN },
    { freq: 620, start: 0.34, duration: 0.26, type: 'square', gain: LONG_TUNE_GAIN },
    { freq: 740, start: 0.72, duration: 0.26, type: 'square', gain: 0.86 },
    { freq: 740, start: 1.06, duration: 0.32, type: 'square', gain: 0.9 },
  ],
  'tune-15': [
    { freq: 523, start: 0, duration: 0.2, type: 'sawtooth', gain: LONG_TUNE_GAIN },
    { freq: 659, start: 0.26, duration: 0.2, type: 'sawtooth', gain: LONG_TUNE_GAIN },
    { freq: 784, start: 0.52, duration: 0.2, type: 'sawtooth', gain: 0.86 },
    { freq: 1047, start: 0.78, duration: 0.24, type: 'sawtooth', gain: 0.88 },
    { freq: 1319, start: 1.1, duration: 0.38, type: 'sawtooth', gain: 0.9 },
  ],
};

const COOLDOWN_MS = 1200;

let audioContext: AudioContext | null = null;
let activeAbort: AbortController | null = null;
let cooldownUntil = 0;
let deferredPlaybackHandle: number | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioContext) {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    audioContext = new Ctx();
  }
  return audioContext;
}

function clampVolume(volume: number): number {
  return Math.min(1, Math.max(0, volume));
}

function cancelActivePlayback(): void {
  activeAbort?.abort();
  activeAbort = null;
}

function cancelDeferredPlayback(): void {
  if (deferredPlaybackHandle !== null) {
    window.clearTimeout(deferredPlaybackHandle);
    deferredPlaybackHandle = null;
  }
}

function canPlayAlertNow(): boolean {
  return !activeAbort && Date.now() >= cooldownUntil;
}

function playSequence(
  tuneId: OnlineOrderTuneId,
  volume: number,
  signal: AbortSignal
): void {
  const ctx = getAudioContext();
  if (!ctx || signal.aborted) return;

  const sequence = TUNE_SEQUENCES[tuneId];
  const masterGain = ctx.createGain();
  masterGain.gain.value = clampVolume(volume);
  masterGain.connect(ctx.destination);

  const oscillators: OscillatorNode[] = [];
  const endTime = ctx.currentTime
    + Math.max(...sequence.map((step) => step.start + step.duration), 0)
    + 0.05;

  sequence.forEach((step) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = step.type ?? 'sine';
    osc.frequency.value = step.freq;
    const t0 = ctx.currentTime + step.start;
    const attack = Math.min(0.004, step.duration * 0.2);
    const release = Math.max(step.duration - attack, 0.01);
    gain.gain.setValueAtTime(0.0001, t0);
    const peakGain = step.gain ?? SHORT_TUNE_GAIN;
    gain.gain.exponentialRampToValueAtTime(peakGain, t0 + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + release);
    osc.connect(gain);
    gain.connect(masterGain);
    osc.start(ctx.currentTime + step.start);
    osc.stop(ctx.currentTime + step.start + step.duration + 0.02);
    oscillators.push(osc);
  });

  const finish = () => {
    oscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch {
        /* already stopped */
      }
    });
    try {
      masterGain.disconnect();
    } catch {
      /* ignore */
    }
    if (activeAbort?.signal === signal) {
      activeAbort = null;
    }
    const sequenceMs = Math.max(...sequence.map((step) => (step.start + step.duration) * 1000), 0);
    cooldownUntil = Date.now() + Math.max(COOLDOWN_MS, sequenceMs + 200);
  };

  signal.addEventListener('abort', finish, { once: true });

  const waitMs = Math.max(0, (endTime - ctx.currentTime) * 1000);
  window.setTimeout(() => {
    if (!signal.aborted) finish();
  }, waitMs);
}

function startPlayback(tuneId: OnlineOrderTuneId, volume: number): void {
  if (!canPlayAlertNow() || volume <= 0) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  cancelActivePlayback();

  const controller = new AbortController();
  activeAbort = controller;

  const run = () => {
    if (controller.signal.aborted) return;
    try {
      playSequence(tuneId, volume, controller.signal);
    } catch {
      cancelActivePlayback();
    }
  };

  if (ctx.state === 'suspended') {
    void ctx.resume().then(run).catch(() => {
      if (activeAbort === controller) activeAbort = null;
    });
    return;
  }

  run();
}

function deferPlayback(run: () => void): void {
  cancelDeferredPlayback();
  deferredPlaybackHandle = window.setTimeout(() => {
    deferredPlaybackHandle = null;
    run();
  }, 0);
}

/**
 * Schedule incoming-order alert. Returns immediately — no await, no queue.
 * Audio work runs on the next task so Firestore/UI callbacks are never blocked.
 */
export function playOnlineOrderAlert(): void {
  if (!canPlayAlertNow()) return;

  deferPlayback(() => {
    if (!canPlayAlertNow()) return;
    const settings = readOnlineOrderAlertSettings();
    startPlayback(settings.tuneId, settings.volume);
  });
}

/** Preview a tune (user-initiated). Deferred slightly so UI click handler stays responsive. */
export function previewOnlineOrderTune(tuneId: OnlineOrderTuneId, volume: number): void {
  cancelDeferredPlayback();
  cancelActivePlayback();
  cooldownUntil = 0;

  deferPlayback(() => {
    startPlayback(tuneId, volume);
  });
}

export function cancelOnlineOrderAlertQueue(): void {
  cancelDeferredPlayback();
  cancelActivePlayback();
}
